import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAvailabilityApi, getCourtsApi, getOpenPlayPublicApi, rsvpOpenPlayApi, getFacilityRulesApi } from '../lib/api';
import { formatHour, formatDate, formatPrice, getStatusColor, getStatusLabel, getTodayPHT, getNowPHT, getCurrentHourPHT } from '../lib/format';
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin, Check, Users, X, Loader2, Clock, AlertCircle, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

export default function PlayerBook() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselected = location.state?.preselected;
  const timelineRef = useRef(null);

  // Detect booking intent (?type=ball_machine)
  const searchParams = new URLSearchParams(location.search);
  const isBallMachine = searchParams.get('type') === 'ball_machine';

  const todayPHT = getTodayPHT();

  const [selectedDate, setSelectedDate] = useState(() => {
    if (preselected?.date) return preselected.date;
    return todayPHT;
  });
  const [availability, setAvailability] = useState(null);
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(preselected?.court_id || null);
  const [selectedStart, setSelectedStart] = useState(preselected?.start_hour || null);
  const [selectedEnd, setSelectedEnd] = useState(preselected?.end_hour || null);
  const [loading, setLoading] = useState(true);
  const [maxBookingHours, setMaxBookingHours] = useState(12);

  // Join Game Panel state
  const [showJoinPanel, setShowJoinPanel] = useState(false);
  const [selectedOpenPlaySlot, setSelectedOpenPlaySlot] = useState(null);
  const [openPlaySession, setOpenPlaySession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(todayPHT + 'T00:00:00');
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  useEffect(() => {
    loadAvailability();
  }, [selectedDate, isBallMachine]);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const [availRes, rulesRes] = await Promise.all([
        getAvailabilityApi(selectedDate, isBallMachine ? 'ball_machine' : undefined),
        getFacilityRulesApi()
      ]);
      setAvailability(availRes.data);
      const courtEntries = Object.values(availRes.data.courts || {});
      setCourts(courtEntries);
      if (!selectedCourt && courtEntries.length > 0) {
        setSelectedCourt(courtEntries[0].court_id);
      }
      // Set max booking hours from facility rules (default 6 if not set)
      if (rulesRes.data?.max_booking_hours) {
        setMaxBookingHours(rulesRes.data.max_booking_hours);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
    setLoading(false);
  };

  const getCourtSlots = () => {
    if (!availability || !selectedCourt) return [];
    const courtData = availability.courts[selectedCourt];
    return courtData?.slots || [];
  };

  // Check if a time slot is in the past (PHT)
  const isSlotInPast = (hour) => {
    const nowPHT = getNowPHT();
    const todayStr = getTodayPHT();
    
    // If selected date is before today, all slots are in the past
    if (selectedDate < todayStr) return true;
    
    // If selected date is after today, no slots are in the past
    if (selectedDate > todayStr) return false;
    
    // Same day - compare hours
    // Slot is in the past if the current hour is greater than or equal to the slot hour
    return getCurrentHourPHT() >= hour;
  };

  const handleSlotClick = async (slot) => {
    const { hour, status } = slot;
    
    // Don't allow clicking past slots
    if (isSlotInPast(hour)) return;
    
    // Handle Open Play slots - show Join Game panel
    if (status === 'open_play' && slot.open_play_session_id) {
      setSelectedOpenPlaySlot(slot);
      setLoadingSession(true);
      setShowJoinPanel(true);
      
      try {
        const res = await getOpenPlayPublicApi(slot.open_play_session_id);
        setOpenPlaySession(res.data);
      } catch (err) {
        toast.error('Failed to load session details');
        setShowJoinPanel(false);
      }
      setLoadingSession(false);
      return;
    }
    
    if (status === 'booked' || status === 'unavailable' || status === 'blocked') return;

    if (selectedStart === null) {
      setSelectedStart(hour);
      setSelectedEnd(hour + 1);
    } else if (hour >= selectedStart && hour < selectedStart + maxBookingHours) {
      setSelectedEnd(hour + 1);
    } else {
      setSelectedStart(hour);
      setSelectedEnd(hour + 1);
    }
  };

  const handleJoinOpenPlay = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await rsvpOpenPlayApi(selectedOpenPlaySlot.open_play_session_id, {
        player_name: playerName.trim(),
        player_phone: playerPhone.trim() || null
      });
      
      // Store registration in localStorage
      localStorage.setItem(`op_${selectedOpenPlaySlot.open_play_session_id}`, JSON.stringify({
        playerId: res.data.player_id,
        status: res.data.status
      }));
      
      if (res.data.status === 'confirmed') {
        toast.success(res.data.message);
        // Navigate to the Open Play RSVP page to show payment instructions
        navigate(`/open-play/${selectedOpenPlaySlot.open_play_session_id}`);
      } else {
        toast.info(res.data.message);
        setShowJoinPanel(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    }
    setSubmitting(false);
  };

  const closeJoinPanel = () => {
    setShowJoinPanel(false);
    setSelectedOpenPlaySlot(null);
    setOpenPlaySession(null);
    setPlayerName('');
    setPlayerPhone('');
  };

  const isSlotSelected = (hour) => {
    return selectedStart !== null && selectedEnd !== null && hour >= selectedStart && hour < selectedEnd;
  };

  const handleContinue = () => {
    if (!selectedStart || !selectedEnd || !selectedCourt) {
      toast.error('Pick a time slot to continue');
      return;
    }
    const courtData = availability?.courts?.[selectedCourt];
    navigate('/confirm', {
      state: {
        court_id: selectedCourt,
        court_name: courtData?.court_name || 'Court',
        date: selectedDate,
        start_hour: selectedStart,
        end_hour: selectedEnd,
        duration_hours: selectedEnd - selectedStart,
        booking_intent: isBallMachine ? 'ball_machine' : 'court',
      }
    });
  };

  const slots = getCourtSlots();

  return (
    <div className="min-h-screen bg-app-bg" data-testid="player-book">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-heading text-lg font-semibold text-gray-900">
            {isBallMachine ? 'Book Training' : 'Pick your time'}
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pb-28">
        {/* Ball Machine intent banner */}
        {isBallMachine && (
          <div
            data-testid="ball-machine-banner"
            className="mt-4 rounded-3xl overflow-hidden border shadow-sm"
            style={{ borderColor: '#D9E6B5' }}
          >
            <div className="px-5 py-4" style={{ backgroundColor: '#F7FBEA' }}>
              <div className="flex items-start gap-3">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden shrink-0"
                  style={{ backgroundColor: '#EEF6C6' }}
                >
                  <img src="/icons/training.png" alt="Ball Machine" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-heading text-lg font-bold" style={{ color: '#6F8C00' }}>Book Training</h2>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider text-white"
                      style={{ backgroundColor: '#6F8C00' }}
                    >
                      NEW
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#4B5563' }}>Everything you need for focused practice.</p>
                </div>
              </div>
            </div>
            <div className="bg-white px-5 py-3 border-t" style={{ borderColor: '#D9E6B5' }}>
              <p className="text-xs uppercase tracking-widest font-semibold text-text-secondary mb-2">Your Training Package Includes</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <span className="flex items-center gap-1.5 text-text-primary">
                  <Check className="w-4 h-4 shrink-0" style={{ color: '#6F8C00' }} />
                  Court Rental
                </span>
                <span className="flex items-center gap-1.5 text-text-primary">
                  <Check className="w-4 h-4 shrink-0" style={{ color: '#6F8C00' }} />
                  Ball Machine
                </span>
                <span className="flex items-center gap-1.5 text-text-primary">
                  <Check className="w-4 h-4 shrink-0" style={{ color: '#6F8C00' }} />
                  80 Pickleballs
                </span>
                <span className="flex items-center gap-1.5 text-text-primary">
                  <Check className="w-4 h-4 shrink-0" style={{ color: '#6F8C00' }} />
                  2 Ball Pickers
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Date Selector */}
        <div className="py-4" data-testid="date-selector">
          <div className="flex items-center gap-2">
            {/* Quick date buttons */}
            <div className="flex gap-2 overflow-x-auto timeline-scroll pb-2 flex-1">
              {dates.map((d) => {
                const isSelected = d === selectedDate;
                const dayObj = new Date(d + 'T00:00:00');
                return (
                  <button
                    key={d}
                    data-testid={`date-${d}`}
                    onClick={() => { setSelectedDate(d); setSelectedStart(null); setSelectedEnd(null); }}
                    className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-center transition-all ${
                      isSelected
                        ? 'bg-brand-teal text-white shadow-md'
                        : 'bg-white border border-black/5 text-gray-600 hover:border-brand-teal/30'
                    }`}
                  >
                    <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">
                      {dayObj.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-semibold">{dayObj.getDate()}</div>
                  </button>
                );
              })}
            </div>
            
            {/* Calendar Picker */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="calendar-picker-btn"
                  className="flex-shrink-0 w-12 h-[58px] rounded-xl bg-white border border-black/5 flex items-center justify-center hover:border-brand-teal/30 hover:bg-gray-50 transition-all"
                  title="Pick a specific date"
                >
                  <CalendarDays className="w-5 h-5 text-gray-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      // Format as local YYYY-MM-DD (avoid UTC shift that pushes PH Wednesday → Tuesday)
                      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      // Double check - don't allow past dates
                      if (dateStr < todayPHT) return;
                      setSelectedDate(dateStr);
                      setSelectedStart(null);
                      setSelectedEnd(null);
                      setCalendarOpen(false);
                    }
                  }}
                  disabled={(date) => {
                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    return dateStr < todayPHT;
                  }}
                  fromDate={new Date(todayPHT + 'T00:00:00')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Show selected date if not in quick dates */}
          {!dates.includes(selectedDate) && (
            <div className="mt-2 px-1">
              <Badge variant="outline" className="bg-brand-teal-tint text-brand-teal border-brand-teal/30">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </Badge>
            </div>
          )}
        </div>

        {/* Court Selector */}
        <div className="mb-4" data-testid="court-selector">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Court</p>
          <div className="flex gap-2 overflow-x-auto timeline-scroll">
            {courts.map((court) => {
              const isSelected = court.court_id === selectedCourt;
              return (
                <button
                  key={court.court_id}
                  data-testid={`court-${court.court_id}`}
                  onClick={() => { setSelectedCourt(court.court_id); setSelectedStart(null); setSelectedEnd(null); }}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                    isSelected
                      ? 'bg-brand-teal-tint border-2 border-brand-teal text-brand-teal'
                      : 'bg-white border border-black/5 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="text-sm font-medium">{court.court_name}</span>
                  {isSelected && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots Grid */}
        <div data-testid="time-slots">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Pick a Time</p>
            <p className="text-xs text-gray-400">Tap consecutive hours to extend your play time</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {slots.map((slot) => {
              const selected = isSlotSelected(slot.hour);
              const isPast = isSlotInPast(slot.hour);
              const isBooked = slot.status === 'booked' || slot.status === 'unavailable';
              const isBlocked = slot.status === 'blocked';
              const isBallMachineTaken = slot.status === 'ball_machine_taken';
              const isDisabled = isPast || isBooked || isBlocked || isBallMachineTaken;
              const isOpenPlay = slot.status === 'open_play';
              const isFull = isOpenPlay && slot.open_play_spots_left === 0;
              
              // Determine slot styling
              let slotClass, labelText;
              if (selected) {
                slotClass = 'bg-brand-teal text-white border-brand-teal shadow-lg scale-[1.03]';
                labelText = 'Selected';
              } else if (isPast) {
                // Fossil gray for past slots
                slotClass = 'bg-[#F1F1F1] border-[#E5E5E5] text-[#9CA3AF] cursor-not-allowed';
                labelText = 'Time Passed';
              } else if (isOpenPlay) {
                slotClass = `bg-open-play-tint border-open-play/20 text-open-play hover:opacity-80 cursor-pointer ${isFull ? 'opacity-70' : ''}`;
                labelText = isFull ? 'Full' : 'Join Game';
              } else if (isBlocked) {
                slotClass = 'bg-status-expired-bg border-status-expired/30 text-status-expired cursor-not-allowed';
                labelText = 'Blocked';
              } else if (isBallMachineTaken) {
                slotClass = 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed';
                labelText = 'Machine in use';
              } else if (isBooked) {
                slotClass = 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed';
                labelText = 'Booked';
              } else {
                // Available
                slotClass = 'bg-white border-status-available/50 text-gray-700 hover:border-brand-teal hover:shadow-sm';
                labelText = 'Available';
              }
              
              return (
                <button
                  key={slot.hour}
                  data-testid={`slot-${slot.hour}`}
                  onClick={() => !isDisabled && handleSlotClick(slot)}
                  disabled={isDisabled}
                  className={`h-[72px] rounded-2xl flex flex-col items-center justify-center font-medium transition-all border ${slotClass}`}
                >
                  <span className="text-sm font-semibold">{formatHour(slot.hour)}</span>
                  <span className="text-[10px] mt-0.5 opacity-70">{labelText}</span>
                  {isOpenPlay && !isFull && !isPast && (
                    <span className="text-[9px] text-open-play">{slot.open_play_spots_left} spots</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-status-available" /> Available</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-status-booked" /> Booked</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-status-open-play" /> Open Play</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" /> Time Passed</span>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      {selectedStart !== null && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-black/5 p-4 pb-6 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] animate-slide-up z-20" data-testid="booking-summary-bar">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {formatHour(selectedStart)}–{formatHour(selectedEnd)}
              </p>
              <p className="text-xs text-gray-500">
                {selectedEnd - selectedStart} hour{selectedEnd - selectedStart > 1 ? 's' : ''}
              </p>
            </div>
            <Button
              data-testid="continue-to-confirm-btn"
              onClick={handleContinue}
              className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full px-8 py-3 font-semibold shadow-lg active:scale-[0.98] transition-all"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Join Game Panel for Open Play */}
      {showJoinPanel && (
        <div className="fixed inset-0 z-50" data-testid="join-game-panel">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeJoinPanel}
          />
          
          {/* Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl animate-slide-up max-h-[85vh] overflow-y-auto">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            
            {/* Close button */}
            <button
              onClick={closeJoinPanel}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              data-testid="close-join-panel-btn"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
            
            {loadingSession ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-open-play" />
              </div>
            ) : openPlaySession ? (
              <div className="px-6 pb-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-open-play-tint mb-3">
                    <Users className="w-7 h-7 text-open-play" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Join Open Play</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatHour(openPlaySession.start_hour)} – {formatHour(openPlaySession.end_hour)}
                  </p>
                </div>
                
                {/* Session Info */}
                <div className="bg-open-play-tint rounded-2xl p-4 mb-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4 text-open-play" />
                      <span className="text-sm">Players</span>
                    </div>
                    <div>
                      <span className="text-lg font-bold text-open-play">{openPlaySession.player_count}</span>
                      <span className="text-gray-500">/{openPlaySession.max_players}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-open-play" />
                      <span className="text-sm">Courts</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{openPlaySession.court_names?.join(', ')}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pt-2 border-t border-open-play/20">
                    <span className="text-sm text-gray-600">Fee</span>
                    <span className="text-lg font-bold text-gray-900">{formatPrice(openPlaySession.price_per_player)}</span>
                  </div>
                </div>
                
                {/* Full Message or Registration Form */}
                {openPlaySession.spots_left === 0 ? (
                  <div className="text-center py-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-status-held-bg mb-3">
                      <AlertCircle className="w-6 h-6 text-status-held" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Session is Full</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {openPlaySession.enable_waitlist 
                        ? 'You can join the waitlist below'
                        : 'Check back later for other sessions'}
                    </p>
                    
                    {openPlaySession.enable_waitlist && (
                      <form onSubmit={handleJoinOpenPlay} className="mt-6 space-y-4 text-left">
                        <div>
                          <Label className="text-sm text-gray-600">Your Name</Label>
                          <Input
                            data-testid="join-name-input"
                            value={playerName}
                            onChange={(e) => setPlayerName(e.target.value)}
                            placeholder="Enter your name"
                            className="rounded-xl mt-1"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full rounded-full py-5 bg-status-held hover:opacity-90 text-white"
                          disabled={submitting}
                          data-testid="join-waitlist-btn"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                          {submitting ? 'Joining...' : 'Join Waitlist'}
                        </Button>
                      </form>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleJoinOpenPlay} className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-600">Your Name</Label>
                      <Input
                        data-testid="join-name-input"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        placeholder="Enter your name"
                        className="rounded-xl mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Phone (optional)</Label>
                      <Input
                        data-testid="join-phone-input"
                        value={playerPhone}
                        onChange={(e) => setPlayerPhone(e.target.value)}
                        placeholder="+63 9XX XXX XXXX"
                        className="rounded-xl mt-1"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full rounded-full py-5 bg-open-play hover:bg-open-play-hover text-white"
                      disabled={submitting}
                      data-testid="join-game-btn"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Joining...</>
                      ) : (
                        <>Join Game</>
                      )}
                    </Button>
                    <p className="text-xs text-center text-gray-400">
                      {openPlaySession.spots_left} spot{openPlaySession.spots_left !== 1 ? 's' : ''} remaining
                    </p>
                  </form>
                )}
                
                {/* Current Players */}
                {openPlaySession.players?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Who's Playing</p>
                    <div className="flex flex-wrap gap-2">
                      {openPlaySession.players.map((player, idx) => (
                        <Badge
                          key={player.id}
                          variant="outline"
                          className="px-3 py-1 bg-gray-50"
                        >
                          {player.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16 text-gray-500">
                Failed to load session details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
